import React, { useRef } from 'react';
import { initComponent } from '../../../../library/react/init-component';

// hooks
import { useInterceptBrowserBackButton } from './hooks/use-intercept-browser-back-button';
import { usePhaseAndPhaseStep } from './hooks/use-phase-and-phase-step';
import { useCalculationNextStep } from './hooks/use-calculation-next-step';
import { useProducts } from './hooks/use-products';
import { useCartMealsLocalStorage } from '../shared/hooks/use-cart-meal-local-storage';

// components
import { ProductCategory } from '../shared/components/ProductCategory';
import { PhaseNavigationBlock } from './PhaseNavigationBlock';
import { PhaseStepNavigation } from './PhaseStepNavigation';
import { PhaseNavigationButtons } from './PhaseNavigationButtons';

// utils
import {
    getPhasePortionCount,
    getLastPhaseStep,
    getContinueToPhaseStepTranslationKey,
    getPhaseStepNameByOrder,
    getFirstPhaseStep,
    getPhaseStepTranslationKey,
    getPrevPhaseStep,
    getFirstIncompletedPhaseIndex,
    getPhaseStepLimitKeyByOrder,
} from '../shared/utils/phase';
import { getMealCount } from '../shared/utils/meals';
import { isOnlyOneCategoryVisible } from '../shared/utils/category';
import {
    FIRST_PHASE_ORDER,
    TRANSLATIONS_PHASE_ORDER_REPLACEMENT,
    TRANSLATIONS_DIET_NAME_REPLACEMENT,
    TRANSLATIONS_PORTION_COUNT_REPLACEMENT,
    TRANSLATIONS_PHASE_STEP_REPLACEMENT,
} from '../shared/constants';
import { useEditMode } from './hooks/use-edit-mode';
import ReactTooltip from 'react-tooltip';

const DietCalculation = ({ defaultData, saveProductApiEndpoint, cartLink, translations }) => {
    const { productCategories, products, cartItemId, dietId, phases, dietName, pickedProductsApi } = defaultData;

    const sortedPhases = phases.sort((phase, nextPhase) => phase.order - nextPhase.order);

    const dietCalculationRef = useRef(null);

    const isEditMode = !!cartItemId;

    const {
        activePhase,
        activePhaseStep,
        phaseIds,
        setNextPhase,
        setNextPhaseStep,
        setPrevPhase,
        setPrevPhaseStep,
        setSpecificPhaseStep,
        phaseStepLimit,
        onPhaseClick,
        resetPhaseStep,
    } = usePhaseAndPhaseStep(cartItemId, dietId, sortedPhases);

    const {
        loadProductsFromStorage,
        saveProductsToStorage,
        removeProductsFromStorage,
        savePickedProductsToStorage,
        loadAllProductsFromStorage,
        isLocalStorageClear,
        savePhaseProductsToStorage,
    } = useCartMealsLocalStorage();

    const loadActivePhaseProductsFromStorage = () => loadProductsFromStorage(cartItemId, dietId, activePhase.id);
    const loadPhaseProductsFromStorage = (phaseId) => loadProductsFromStorage(cartItemId, dietId, phaseId);

    const {
        pickedProducts,
        savedProducts,
        onDecrementProduct,
        onIncrementProduct,
        onProductAmountChange,
        saveProducts,
        resetPickedProducts,
        setPickedProducts,
        productAvailabilityMap,
        filteredCategoryProductsByPhaseStep,
        updateOrder,
        pickedPhaseProductsCountMap,
        updateAvailabilityMap,
        phaseStepProductExceededAvailabilityMap,
    } = useProducts(
        products,
        cartItemId,
        dietId,
        activePhase.id,
        activePhaseStep,
        loadProductsFromStorage,
        saveProductsToStorage,
        !!cartItemId,
        saveProductApiEndpoint,
        phaseStepLimit,
        phaseIds,
        loadAllProductsFromStorage,
        translations,
        savePhaseProductsToStorage,
        loadPhaseProductsFromStorage,
        sortedPhases
    );

    useEditMode(
        savePickedProductsToStorage,
        isLocalStorageClear,
        cartItemId,
        dietId,
        phaseIds,
        sortedPhases,
        activePhase,
        setPickedProducts,
        pickedProductsApi
    );

    const isPhaseMode = dietId === null;

    const pickedProductsCount = getMealCount(pickedProducts);
    const hasPickedProductExceedAvailability = productAvailabilityMap
        ? pickedProducts.some((pickedProduct) =>
              productAvailabilityMap[pickedProduct.id] ? productAvailabilityMap[pickedProduct.id].available < 0 : false
          )
        : false;

    // const hasPhaseStepMealsExceeded = pickedProductsCount > phaseStepLimit;
    const hasPhaseStepMealsCompleted = pickedProductsCount === phaseStepLimit;

    const isLastPhase = activePhase.order === phases.length - 1;
    const isLastPhaseStep = activePhaseStep === getLastPhaseStep(activePhase);

    const isDietModeAndLastPhase = !isPhaseMode && isLastPhase;
    const isDietModeAndLastPhaseAndLastPhaseStep = isDietModeAndLastPhase && isLastPhaseStep;
    const isDietModeAndLastPhaseStep = !isPhaseMode && isLastPhaseStep;
    const isDietModeAndFirstPhase = !isPhaseMode && activePhase.order === FIRST_PHASE_ORDER;
    const isPhaseModeAndLastPhaseStep = isPhaseMode && isLastPhaseStep;

    useCalculationNextStep(
        savedProducts,
        isDietModeAndLastPhaseAndLastPhaseStep,
        isPhaseModeAndLastPhaseStep,
        isDietModeAndLastPhaseStep,
        setNextPhase,
        setNextPhaseStep,
        resetPhaseStep,
        resetPickedProducts,
        phaseStepLimit,
        activePhaseStep,
        isDietModeAndLastPhase,
        isPhaseMode,
        saveProductApiEndpoint,
        dietId,
        activePhase,
        cartLink,
        removeProductsFromStorage,
        cartItemId,
        phaseIds,
        loadAllProductsFromStorage
    );

    useInterceptBrowserBackButton(isPhaseMode, isDietModeAndFirstPhase, setPrevPhase, resetPhaseStep);

    const onContinueButtonClick = () => {
        // trigger useEffectu v useCalculationNextStep pro pokročení dalšího stavu kalkulačky (nemůže řešit imperativně, je potřeba nejdříve přepsat savedProducts)
        saveProducts(activePhase.id, activePhaseStep);

        if (dietCalculationRef && dietCalculationRef.current) {
            dietCalculationRef.current.scrollIntoView();
        }
    };

    const onCompleteEditationButtonClick = () => {
        window.location.href = cartLink;
    };

    const onAutoPickProductsButtonClick = () => {
        if (!activePhase) {
            return;
        }

        const phaseStepNameByOrder = getPhaseStepNameByOrder(activePhaseStep);

        const updatedProducts = activePhase.defaultMenu
            .filter((defaultMenuProduct) => defaultMenuProduct.dishTypes.some((dish) => dish === phaseStepNameByOrder))
            .map((defaultMenuProduct) => ({
                id: defaultMenuProduct.productId,
                amount: defaultMenuProduct.count,
                title: defaultMenuProduct.productName,
            }));

        updateAvailabilityMap(updatedProducts);
        setPickedProducts(updatedProducts);
        saveProductsToStorage(cartItemId, dietId, activePhase.id, activePhaseStep, updatedProducts);

        if (isEditMode) {
            updateOrder();
        }
    };

    const getCategoryProducts = (productCategoryId) => {
        const categoryProducts = filteredCategoryProductsByPhaseStep
            ? filteredCategoryProductsByPhaseStep.filter((product) => product.categoryId === productCategoryId)
            : [];

        return categoryProducts.map((product) => {
            const pickedProduct = pickedProducts.find((pickedProduct) => pickedProduct.id === product.id);

            return { ...product, amount: pickedProduct ? pickedProduct.amount : 0 };
        });
    };

    const expandCategory = isOnlyOneCategoryVisible(productCategories, getCategoryProducts);

    const continueButtonText = (
        <span>
            {isDietModeAndLastPhaseAndLastPhaseStep || isPhaseModeAndLastPhaseStep
                ? translations['eshop.configurator.action.submit_data']
                : isDietModeAndLastPhaseStep
                ? `${translations['eshop.configurator.message_part.done']}. ${translations[
                      'eshop.configurator.message_part.proceed_to_phase'
                  ].replace(TRANSLATIONS_PHASE_ORDER_REPLACEMENT, activePhase.order + 2)}`
                : `${translations['eshop.configurator.message_part.done']}. ${
                      translations[getContinueToPhaseStepTranslationKey(activePhaseStep + 1)]
                  }`}
        </span>
    );

    const isFirstPhaseStep = getFirstPhaseStep(activePhase) === activePhaseStep;
    const firstIncompletedPhaseIndex = getFirstIncompletedPhaseIndex(
        sortedPhases,
        loadPhaseProductsFromStorage,
        productAvailabilityMap
    );

    const isFirstPhase = sortedPhases[0].id === activePhase.id;

    const phaseStepTranslated =
        translations[getPhaseStepTranslationKey(getPrevPhaseStep(activePhase, activePhaseStep))].toLowerCase();

    const phaseTranslated = translations['eshop.configurator.phase_name_for'].replace(
        TRANSLATIONS_PHASE_ORDER_REPLACEMENT,
        activePhase.order
    );

    const phaseStepGoBackButtonTitle = translations['eshop.configurator.action.phase_step_go_back'].replace(
        TRANSLATIONS_PHASE_STEP_REPLACEMENT,
        isFirstPhaseStep ? phaseTranslated : phaseStepTranslated
    );

    const currentPickedProdctsAmount = pickedProducts ? getMealCount(pickedProducts) : 0;
    const currentPhaseStepLimit = activePhase ? activePhase[getPhaseStepLimitKeyByOrder(activePhaseStep)] : 0;

    const isAllPhasesComplete = phases.every((phase) =>
        pickedPhaseProductsCountMap ? pickedPhaseProductsCountMap[phase.id] === getPhasePortionCount(phase) : 0
    );

    return (
        <div className="calculation" ref={dietCalculationRef}>
            <ReactTooltip />

            <div className="calculation__description">
                <div>
                    {translations['eshop.configurator.picked_package'].replace(
                        TRANSLATIONS_DIET_NAME_REPLACEMENT,
                        dietName
                    )}
                </div>
                <div>{translations['eshop.configurator.info.process_description']}</div>
            </div>
            <div className="phase-navigation-wrapper">
                <div className="phase-navigation">
                    {sortedPhases.map((phase, index) => {
                        const pickedPhaseProductsCount = pickedPhaseProductsCountMap
                            ? pickedPhaseProductsCountMap[phase.id]
                            : 0;

                        const isPhaseWithError =
                            (phaseStepProductExceededAvailabilityMap[phase.id] &&
                                Object.keys(phaseStepProductExceededAvailabilityMap[phase.id]).some(
                                    (phaseStep) => phaseStepProductExceededAvailabilityMap[phase.id][phaseStep]
                                )) ||
                            false;

                        return (
                            <PhaseNavigationBlock
                                key={index}
                                name={translations['eshop.configurator.phase_name'].replace(
                                    TRANSLATIONS_PHASE_ORDER_REPLACEMENT,
                                    phase.order + 1
                                )}
                                portionCount={getPhasePortionCount(phase)}
                                isSelected={activePhase.id === phase.id}
                                translations={translations}
                                onPhaseClick={() => onPhaseClick(phase.id)}
                                isDisabled={index > firstIncompletedPhaseIndex}
                                phaseItemCount={pickedPhaseProductsCount}
                                isPhaseWithError={isPhaseWithError}
                            />
                        );
                    })}
                </div>
            </div>
            <h2 className="h3 diet-calculation__diet-name">
                {translations['eshop.configurator.message_part.phase']
                    .replace(TRANSLATIONS_DIET_NAME_REPLACEMENT, dietName)
                    .replace(TRANSLATIONS_PHASE_ORDER_REPLACEMENT, activePhase.order + 1)
                    .replace(TRANSLATIONS_PORTION_COUNT_REPLACEMENT, getPhasePortionCount(activePhase))}
            </h2>
            <div className="phase-info">
                <span>
                    <span className="phase-info__icon">
                        <img src="../../../../img/theme/icons/info.svg" alt="info" />
                    </span>
                    <span>{activePhase ? activePhase.info : ''}</span>
                </span>
            </div>
            <div>
                <PhaseStepNavigation
                    phaseSteps={activePhase ? activePhase.steps : []}
                    setSpecificPhaseStep={setSpecificPhaseStep}
                    phase={activePhase}
                    activePhaseStep={activePhaseStep}
                    productAvailabilityMap={productAvailabilityMap}
                    isCurrentPhaseStepCompleted={!hasPhaseStepMealsCompleted || hasPickedProductExceedAvailability}
                    loadActivePhaseProductsFromStorage={loadActivePhaseProductsFromStorage}
                    translations={translations}
                    onCompleteEditationButtonClick={onCompleteEditationButtonClick}
                    phaseStepProductExceededAvailabilityMap={phaseStepProductExceededAvailabilityMap}
                />
            </div>
            <div className="product-category-wrapper">
                {productCategories && productCategories.length ? (
                    <>
                        <PhaseNavigationButtons
                            phaseStepBackButtonText={phaseStepGoBackButtonTitle}
                            showPhaseStepBackButton={!(isFirstPhaseStep && isFirstPhase)}
                            translations={translations}
                            onPhaseStepBackButtonClick={isFirstPhaseStep ? setPrevPhase : setPrevPhaseStep}
                            onAutoPickProductsButtonClick={onAutoPickProductsButtonClick}
                            hasPhaseStepMealsCompleted={hasPhaseStepMealsCompleted}
                            hasPickedProductExceedAvailability={hasPickedProductExceedAvailability}
                            isEditMode={isEditMode}
                            onContinueButtonClick={onContinueButtonClick}
                            continueButtonText={continueButtonText}
                            onCompleteEditationButtonClick={onCompleteEditationButtonClick}
                            isAllPhasesComplete={isAllPhasesComplete}
                        />

                        {productCategories.map((productCategory) => {
                            const categoryProducts = getCategoryProducts(productCategory.id);

                            return categoryProducts && categoryProducts.length > 0 ? (
                                <ProductCategory
                                    key={productCategory.id}
                                    category={productCategory}
                                    products={categoryProducts}
                                    onDecrementProduct={onDecrementProduct}
                                    onIncrementProduct={onIncrementProduct}
                                    onProductAmountChange={onProductAmountChange}
                                    phaseStep={activePhaseStep}
                                    defaultExpanded={expandCategory}
                                    translations={translations}
                                    productAvailabilityMap={productAvailabilityMap}
                                    remainingProductsToCompleteCount={
                                        currentPhaseStepLimit - currentPickedProdctsAmount
                                    }
                                />
                            ) : null;
                        })}
                    </>
                ) : (
                    <div>{translations['eshop.configurator.info.no_category_found']}</div>
                )}
            </div>

            <div className="buttons-wrapper">
                <div className="text-center">
                    {isFirstPhaseStep && isFirstPhase ? null : (
                        <button
                            className="btn btn--md btn-sm--xl btn--outline-purple btn--white phase-navigation-button phase-navigation-button--back"
                            onClick={isFirstPhaseStep ? setPrevPhase : setPrevPhaseStep}
                        >
                            {/* prettier-ignore */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" viewBox="0 0 10 15"><g><g transform="rotate(-270 5.5 7.5)"><g><g><path fill="none" stroke="#6b3389" strokeMiterlimit="20" strokeWidth="2" d="M-1.25 4.5v0l6.75 6.75v0l6.75-6.75v0"/></g></g></g></g></svg>
                            <span>{phaseStepGoBackButtonTitle}</span>
                        </button>
                    )}
                </div>
                <div className="phase-pick-buttons-wrapper">
                    <button
                        className="btn btn--md btn-sm--xl btn--outline-green btn--outline-green btn--white phase-navigation-button autopick-button"
                        onClick={onAutoPickProductsButtonClick}
                    >
                        <span>{translations['eshop.configurator.action.prefill_step_as_recommended']}</span>
                    </button>
                    {isEditMode ? (
                        <button
                            className={`btn btn--md btn-sm--xl btn--green diet-calculation-send-offer${
                                !isAllPhasesComplete || hasPickedProductExceedAvailability
                                    ? ' diet-calculation-button--disabled'
                                    : ''
                            }`}
                            onClick={
                                !isAllPhasesComplete || hasPickedProductExceedAvailability
                                    ? undefined
                                    : onCompleteEditationButtonClick
                            }
                            data-tip={translations['eshop.configurator.action.complete_diet_editation_disabled']}
                            data-tip-disable={isAllPhasesComplete && !hasPickedProductExceedAvailability}
                        >
                            <span>{translations['eshop.configurator.action.complete_diet_editation']}</span>
                        </button>
                    ) : (
                        <button
                            className="btn btn--md btn-sm--xl btn--green diet-calculation-send-offer"
                            onClick={onContinueButtonClick}
                            disabled={!hasPhaseStepMealsCompleted || hasPickedProductExceedAvailability}
                        >
                            <span>{continueButtonText}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const initDietCalculation = (container, props) => initComponent(container, <DietCalculation {...props} />);
